import React, { ComponentType, forwardRef, useCallback } from 'react';
import { List, StackProps } from '@mui/material';

import { Stack } from '../Stack';
import { NoOptions } from './NoOptions';
import { Search, SearchProps } from './Search';
import { ButtonsContainer, ButtonsContainerProps } from './ButtonsContainer';
import { Item, DropdownOption, ItemProps } from './Item';
import { SelectAll } from './SelectAll';
import { TypographyProps } from '../Typography';
import { boxShadows } from '../../palette';


export type DropdownProps = {
    isMulti?: boolean;
    allowSelectAll?: boolean;
    options: DropdownOption[];
    placeholder?: SearchProps['placeholder'];
    needNoOptionsMessage?: boolean;
    noOptionsMessage?: string;
    inputValue?: SearchProps['value'];
    onInputChange?: SearchProps['onChange'];
    value: DropdownOption | DropdownOption[];
    onChange: (value: DropdownOption | DropdownOption[]) => void;
    buttons?: ComponentType;
    withButtonsContainerBg?: ButtonsContainerProps['withBackground'];
    sx?: StackProps['sx'];
    itemSx?: ItemProps['sx'];
    itemTypographyProps?: TypographyProps;
    onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
};

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
    (
        {
            isMulti,
            allowSelectAll,
            options,
            onChange,
            value,
            inputValue,
            onInputChange,
            buttons: Buttons,
            placeholder = 'Поиск',
            needNoOptionsMessage,
            noOptionsMessage = 'По вашему запросу ничего не найдено',
            withButtonsContainerBg,
            sx = {},
            itemSx,
            itemTypographyProps,
            onMouseLeave,
        },
        ref,
    ) => {
        const onSelect = useCallback(
            (o: DropdownOption) =>
                onChange(
                    !isMulti
                        ? o
                        : Array.isArray(value)
                            ? value.some(v => v.value === o.value)
                                ? value.filter(v => v.value !== o.value)
                                : value.concat(o)
                            : [],
                ),
            [onChange, isMulti, value],
        );

        return (
            <Stack
                onMouseLeave={onMouseLeave}
                ref={ref}
                sx={{
                    backgroundColor: 'grayscale.shade0',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: boxShadows.shadow1,
                    ...sx,
                }}
            >
                {onInputChange && (
                    <Search
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={onInputChange}
                    />
                )}
                {options.length > 0 && (
                    <List sx={{ overflow: 'auto', p: 2 }}>
                        <Stack spacing={0.5}>
                            {isMulti && allowSelectAll && (
                                <SelectAll
                                    value={value as DropdownOption[]}
                                    options={options}
                                    onChange={onChange}
                                />
                            )}
                            {options.map(o => (
                                <Item
                                    key={o.value}
                                    data={o}
                                    onSelect={onSelect}
                                    isMulti={isMulti}
                                    isSelected={
                                        Array.isArray(value)
                                            ? value.some(v => v.value === o.value)
                                            : o.value === value?.value
                                    }
                                    sx={itemSx}
                                    itemTypographyProps={itemTypographyProps}
                                />
                            ))}
                        </Stack>
                    </List>
                )}
                {needNoOptionsMessage && options.length === 0 && (
                    <NoOptions allowInput={!!onInputChange}>
                        {noOptionsMessage}
                    </NoOptions>
                )}
                {Buttons && (
                    <ButtonsContainer withBackground={withButtonsContainerBg}>
                        <Buttons />
                    </ButtonsContainer>
                )}
            </Stack>
        );
    },
);

export type { DropdownOption };
